import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { theme } from '@/styles/theme';
import { Container } from '@/components/ui/Container';
import { Navigation } from './Navigation';
import { useHeaderMode } from '@/context/HeaderModeContext';
import logoImage from '../../assets/images/logo-kph-blauw.png';

const HeaderWrapper = styled(motion.header)<{ $scrolled: boolean; $hidden: boolean; $overHero: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${theme.zIndices.sticky};

  /* Consistent height, shared with the main-content offset and the hero pull-up. */
  --header-height: var(--kph-header-offset);
  height: var(--header-height);

  /* Background: integrated (transparent scrim) over the hero, solid otherwise. */
  background: ${({ $scrolled, $overHero }) =>
    $overHero
      ? 'linear-gradient(180deg, rgba(16, 19, 31, 0.55) 0%, rgba(16, 19, 31, 0) 100%)'
      : $scrolled
        ? 'rgba(255, 255, 255, 0.99)'
        : 'rgba(255, 255, 255, 0.96)'};
  backdrop-filter: ${({ $overHero }) => ($overHero ? 'none' : 'blur(12px)')};
  -webkit-backdrop-filter: ${({ $overHero }) => ($overHero ? 'none' : 'blur(12px)')};

  /* Border and shadows */

  box-shadow: ${({ $scrolled, $overHero }) =>
    $overHero
      ? 'none'
      : $scrolled
        ? '0 4px 12px rgba(0, 0, 0, 0.08)'
        : '0 1px 3px rgba(0, 0, 0, 0.04)'};

  /* Subtle hairline under the bar */
  border-bottom: 1px solid ${({ $overHero }) =>
    $overHero ? 'rgba(255, 255, 255, 0.10)' : 'rgba(15, 52, 96, 0.08)'};

  transition: background ${theme.transitions.base}, box-shadow ${theme.transitions.base}, border-color ${theme.transitions.base}, transform 300ms cubic-bezier(0.4, 0, 0.2, 1);

  /* Scroll direction behavior */
  transform: translateY(${({ $hidden }) => ($hidden ? '-100%' : '0')});

  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    transition: background ${theme.transitions.base}, box-shadow ${theme.transitions.base};
    transform: none !important;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--header-height);
  position: relative;
  z-index: 1;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  text-decoration: none;
  position: relative;
  transition: transform ${theme.transitions.base};

  /* Keyboard focus */
  &:focus-visible {
    outline: 2px solid ${theme.colors.primary.gold};
    outline-offset: 4px;
    border-radius: ${theme.borderRadius.md};
  }

  &:hover {
    transform: translateY(-2px);

    img {
      filter: drop-shadow(0 4px 8px rgba(212, 175, 55, 0.4));
    }
  }

  &:active {
    transform: translateY(0);
  }

  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const LogoImage = styled.img<{ $onDark?: boolean }>`
  height: 48px;
  width: auto;
  object-fit: contain;
  transition: filter ${theme.transitions.smooth};
  filter: ${({ $onDark }) =>
    $onDark
      ? 'brightness(0) invert(1) drop-shadow(0 2px 6px rgba(16, 19, 31, 0.5))'
      : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'};

  @media (max-width: ${theme.breakpoints.md}) {
    height: 40px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};

  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

const LogoTitle = styled.span<{ $onDark?: boolean }>`
  font-family: ${theme.fonts.heading};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: ${theme.fontWeights.bold};
  line-height: 1;
  letter-spacing: -0.02em;
  position: relative;
  padding-bottom: ${theme.spacing[1]};

  color: ${({ $onDark }) => ($onDark ? theme.colors.neutral.white : theme.colors.primary.navy)};
  text-shadow: ${({ $onDark }) => ($onDark ? '0 1px 10px rgba(16, 19, 31, 0.45)' : 'none')};
  transition: color ${theme.transitions.base};

  /* Gold underline grows on logo hover */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: ${theme.colors.primary.gold};
    transition: width ${theme.transitions.base};
  }

  ${Logo}:hover &::after {
    width: 100%;
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    font-size: ${theme.fontSizes.xl};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      transition: none;
    }
  }
`;

const LogoSubtitle = styled.span<{ $onDark?: boolean }>`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${({ $onDark }) => ($onDark ? theme.colors.primary.lightGold : theme.colors.primary.royalBlue)};
  letter-spacing: 0.5px;
  opacity: 0.85;
  transition: color ${theme.transitions.smooth}, opacity ${theme.transitions.smooth};

  ${Logo}:hover & {
    color: ${theme.colors.primary.gold};
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.lg}) {
    display: none;
  }
`;

const MobileMenuButton = styled.button<{ $isOpen: boolean; $onDark?: boolean }>`
  display: none;
  padding: ${theme.spacing[2]};
  color: ${({ $isOpen, $onDark }) =>
    $isOpen
      ? theme.colors.neutral.white
      : $onDark
        ? theme.colors.neutral.white
        : theme.colors.primary.navy};
  background: ${({ $isOpen }) =>
    $isOpen ? theme.colors.primary.navy : 'transparent'};
  border: 2px solid ${({ $isOpen }) =>
    $isOpen ? theme.colors.primary.navy : 'transparent'};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  z-index: 1;
  transition: all ${theme.transitions.base};
  flex-shrink: 0;
  min-width: 44px;
  min-height: 44px;
  position: relative;

  @media (hover: hover) {
    &:hover:not([aria-expanded='true']) {
      border-color: ${theme.colors.primary.gold};
    }
  }

  &:focus-visible:not([aria-expanded='true']) {
    border-color: ${theme.colors.primary.gold};
  }

  /* Keyboard focus */
  &:focus-visible {
    outline: 2px solid ${theme.colors.primary.gold};
    outline-offset: 2px;
  }

  svg {
    transition: transform ${theme.transitions.base};
    display: block;
    position: relative;
    z-index: 1;
  }

  /* Rotate hamburger to X */
  ${({ $isOpen }) =>
    $isOpen &&
    `
    svg {
      transform: rotate(90deg);
    }
  `}

  @media (max-width: ${theme.breakpoints.lg}) {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: background ${theme.transitions.base}, color ${theme.transitions.base};

    svg {
      transform: none !important;
      transition: none;
    }
  }
`;

/* No backdrop-filter: a full-screen blur forces continuous GPU repaints on
   mobile and made the open/close animation stutter. A plain scrim is enough. */
const MobileMenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(26, 26, 46, 0.6);
  z-index: ${theme.zIndices.modalBackdrop};
`;

const MobileMenu = styled(motion.nav)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100vh;
  height: 100dvh;
  width: 85%;
  max-width: 400px;
  background: ${theme.colors.neutral.white};
  z-index: ${theme.zIndices.modal};
  overflow-y: auto;
  box-shadow: ${theme.shadows['2xl']};

  /* Safe area insets */
  padding-right: max(0px, env(safe-area-inset-right));
  padding-top: max(0px, env(safe-area-inset-top));
  padding-bottom: max(0px, env(safe-area-inset-bottom));

  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    max-width: none;
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[5]} ${theme.spacing[6]};
  background: ${theme.colors.primary.navy};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  min-height: var(--header-height);
  position: relative;
`;

const MobileMenuLogo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const MobileMenuLogoImage = styled.img`
  height: 40px;
  width: auto;
  object-fit: contain;
  filter: brightness(0) invert(1);
`;

const MobileMenuTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const MobileMenuTitleText = styled.span`
  font-family: ${theme.fonts.heading};
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.neutral.white};
  line-height: 1.2;
`;

const MobileMenuSubtitle = styled.span`
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.primary.lightGold};
  opacity: 0.95;
`;

const MobileCloseButton = styled.button`
  padding: ${theme.spacing[2]};
  color: ${theme.colors.neutral.white};
  background: transparent;
  border: none;
  border-radius: ${theme.borderRadius.full};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 44px;
  min-height: 44px;

  /* Keyboard focus */
  &:focus-visible {
    outline: 2px solid ${theme.colors.primary.gold};
    outline-offset: 2px;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: ${theme.borderRadius.full};
    background: rgba(255, 255, 255, 0.1);
    opacity: 0;
    transform: scale(0);
    transition: all ${theme.transitions.fast};
  }

  svg {
    position: relative;
    z-index: 1;
    transition: all ${theme.transitions.fast};
  }

  @media (hover: hover) {
    &:hover {
      color: ${theme.colors.primary.gold};

      &::before {
        opacity: 1;
        transform: scale(1);
      }

      svg {
        transform: rotate(90deg);
      }
    }
  }

  &:active {
    transform: scale(0.95);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: color ${theme.transitions.base};

    &::before {
      transition: opacity ${theme.transitions.base};
    }

    svg {
      transform: none !important;
      transition: none;
    }
  }
`;

const MobileMenuContent = styled.div`
  padding: ${theme.spacing[8]} ${theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { overHero } = useHeaderMode();

  // Integrated (light-on-transparent) treatment while over the hero, but never
  // when the mobile drawer is open (that has its own dark panel).
  const dark = overHero && !mobileMenuOpen;
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // Whether the menu is closing because a nav link was clicked: the scroll
  // lock must then release to the top of the new page, not the old offset.
  const navigatingRef = useRef(false);

  // Scroll detection with direction
  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          // Set scrolled state
          setScrolled(currentScrollY > 50);

          // Set hidden state based on scroll direction
          // Hide header when scrolling down past 100px
          // Show header when scrolling up
          if (currentScrollY > 100 && currentScrollY > lastScrollY.current) {
            setHidden(true);
          } else {
            setHidden(false);
          }

          lastScrollY.current = currentScrollY;
          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open. position: fixed instead of
  // overflow: hidden: iOS ignores the latter and keeps the visual viewport
  // scrollable, which leaves the fixed menu's tap targets offset from where
  // they are drawn. Pinning the body at its scroll offset keeps hit-testing
  // aligned; the offset is restored on close.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const scrollY = window.scrollY;
    const { body } = document;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      // After navigating, land at the top of the new page; otherwise put the
      // visitor back where they were.
      window.scrollTo(0, navigatingRef.current ? 0 : scrollY);
      navigatingRef.current = false;
    };
  }, [mobileMenuOpen]);

  // Focus management for mobile menu
  useEffect(() => {
    if (mobileMenuOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    } else if (!mobileMenuOpen && menuButtonRef.current && document.activeElement !== document.body) {
      // Only return focus if the active element is not the body (which means something had focus)
      menuButtonRef.current.focus();
    }
  }, [mobileMenuOpen]);

  // Keyboard navigation - ESC to close menu
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  const handleMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleOverlayClick = () => {
    setMobileMenuOpen(false);
  };

  const handleNavigate = () => {
    navigatingRef.current = true;
    setMobileMenuOpen(false);
  };

  return (
    <HeaderWrapper
      $scrolled={scrolled}
      $hidden={overHero ? false : hidden}
      $overHero={dark}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <Container>
        <HeaderContent>
          <Logo to="/" aria-label="KPH Sint-Cecilia Poperinge - Home">
            <LogoImage src={logoImage} alt="" $onDark={dark} />
            <LogoText>
              <LogoTitle $onDark={dark}>KPH Sint-Cecilia</LogoTitle>
              <LogoSubtitle $onDark={dark}>Poperinge</LogoSubtitle>
            </LogoText>
          </Logo>

          <DesktopNav>
            <Navigation onDark={dark} />
          </DesktopNav>

          <MobileMenuButton
            ref={menuButtonRef}
            onClick={handleMenuToggle}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            $isOpen={mobileMenuOpen}
            $onDark={dark}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </MobileMenuButton>
        </HeaderContent>
      </Container>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <MobileMenuOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={handleOverlayClick}
              aria-hidden="true"
              /* Tap-transparent the moment it starts closing, so the next tap
                 reaches the page instead of the dying overlay. */
              style={{ pointerEvents: mobileMenuOpen ? 'auto' : 'none' }}
            />
            <MobileMenu
              id="mobile-menu"
              role="dialog"
              aria-label="Mobile navigation menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              /* Fast transform-only tween: the old spring overshot and kept
                 the panel (and its tap targets) in motion for ~700ms. */
              transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              style={{ pointerEvents: mobileMenuOpen ? 'auto' : 'none' }}
            >
              <MobileMenuHeader>
                <MobileMenuLogo>
                  <MobileMenuLogoImage src={logoImage} alt="" />
                  <MobileMenuTitle>
                    <MobileMenuTitleText>KPH Sint-Cecilia</MobileMenuTitleText>
                    <MobileMenuSubtitle>Poperinge</MobileMenuSubtitle>
                  </MobileMenuTitle>
                </MobileMenuLogo>
                <MobileCloseButton
                  ref={closeButtonRef}
                  onClick={handleMenuToggle}
                  aria-label="Close menu"
                >
                  <X size={24} />
                </MobileCloseButton>
              </MobileMenuHeader>
              <MobileMenuContent>
                <Navigation
                  mobile
                  onNavigate={handleNavigate}
                />
              </MobileMenuContent>
            </MobileMenu>
          </>
        )}
      </AnimatePresence>
    </HeaderWrapper>
  );
};
